// Creative Library — grid + filters; the detail drawer is `./library-drawer`.
// Reads /creatives and /productions. Poster styling (posterBg/Fg/text)
// is legacy presentation metadata not exposed by the API; we render a neutral
// placeholder until a poster-config flow lands in Phase D. Lifetime metrics
// (impressions, CTR, ROAS, tickets) come from /reporting in a future pass —
// for now we render '—'.
//
// Canonical creative fields used by this screen:
//   c.production    → c.production_id  (joined to /productions by id)
//   c.mediaType     → c.media_type
//   c.uploaded      → c.created_at
//   c.lastUsed      → c.last_used_at
//   c.creator       → c.created_by (UserId; rendered as id until /users lookup added)
//   c.impr/ctr/roas → not on canonical; rendered as '—' until a reporting roll-up hook lands
//   c.usedIn        → not on canonical; rendered as '—'
//   c.isNew         → wizard-flash flag, kept via flashId/CREATIVES lookup
//
// Canonical production fields used by this screen:
//   p.title  → p.name
//   p.poster*/posterBg/posterFg → not on canonical Production; placeholder used

import type { Creative, Production } from '@/api/types';
import { displayAnimationExceedsLimit } from '@/lib/media-validation/display';
import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useAdaptive } from '@/lib/responsive';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icons, ChannelPill } from '@/components/primitives';
import { StatusPill } from '@/components/status-pill';
import { Dropdown, type DropdownOption } from '@/components/dropdowns';
import { useVirtualGrid } from '@/components/virtual-table';
import { useCreatives, useProductions } from '@/api/hooks';
import { downloadCreativeCsv } from '@/api/creative-export';
import { ScopeSearchPicker } from '@/components/scope-search-picker';
import { fmtDate, fmtDateInput } from '@/lib/datetime';
import { creativeSpecDisplay } from './creative-config';
import { libraryToAssets, type LibraryPreviewAsset } from '@/lib/creative-library-assets';
import { campaignStatusLabel, campaignPinKind } from '@/lib/campaign-status';

/** A creative's status in the words the tiles and the KPI strip use. */
const CREATIVE_STATUS_WORDS: Record<Creative['status'], string> = {
  draft: 'Draft',
  review: 'In review',
  approved: 'Approved',
  archived: 'Archived',
};
import { PageActions } from '@/components/page-actions';
import {
  CreativeDetailDrawer,
  LIB_CHANNEL_OPTS,
  VersionTag,
  type CreativeDrawerTab,
  type LibraryChannelFilter,
} from './library-drawer';

/** The library's status filter: every live status (archived hidden), or one. */
type LibraryStatusFilter = 'all' | Creative['status'];

const LIB_STATUS_OPTS: readonly DropdownOption<LibraryStatusFilter>[] = [
  { id: 'all',      label: 'All statuses' },
  { id: 'approved', label: 'Approved' },
  { id: 'review',   label: 'In review' },
  { id: 'draft',    label: 'Draft' },
  { id: 'archived', label: 'Archived' },
];

// Production poster placeholder — no posterBg/Fg/text in the canonical
// Production. Generates a stable hue per production id so the grid still
// reads as a colorful collection.
interface PosterStyle {
  bg: string;
  fg: string;
  initials: string;
}

/** The production a card names: its id seeds the poster hue, its name the initials. */
type CardProduction = Pick<Production, 'id' | 'name'>;

function posterStyleFor(production: CardProduction | null | undefined): PosterStyle {
  if (!production) return { bg: 'var(--ink-3)', fg: '#fff', initials: '—' };
  const seed = (production.id || production.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue = seed % 360;
  return {
    bg: `oklch(70% 0.12 ${hue})`,
    fg: '#0b0b0b',
    initials: (production.name || '').split(/\s+/).slice(0, 3).map(w => w[0] || '').join('').toUpperCase().slice(0, 3) || '—',
  };
}

/// The 1:1 asset for a Meta creative, or the first available asset for CTV and
/// Display. `view_url` is a presigned R2 GET that `GET /creatives` already
/// hydrates, so this needs no extra request — but it is absent in local dev
/// where the R2 S3 secrets are unset, which is why the generated poster stays
/// as the fallback rather than being deleted.
function primaryPreview(creative: Creative): LibraryPreviewAsset | null {
  if (creative.channel === 'meta') {
    const assets = libraryToAssets(creative, null, 'meta');
    return assets.sq || assets.fs || assets.story || null;
  }
  if (creative.channel === 'ctv') return libraryToAssets(creative, null, 'ctv').asset || null;
  const assets = libraryToAssets(creative, null, 'dsp');
  // A Display banner is itself the preview record (`libraryToAssets`); it has no
  // `.asset`, which is what this read, so every Display card showed the poster.
  return (assets.banners || []).find(banner => !!banner?.url) || null;
}


/** Productions by id; a creative's production may be missing from the list. */
type ProductionMap = Readonly<Record<string, Production>>;

export interface CreativeCardProps {
  c: Creative;
  production: CardProduction | null | undefined;
  onOpen: () => void;
}

function CreativeCard({ c, production, onOpen }: CreativeCardProps) {
  const poster = posterStyleFor(production);
  const preview = primaryPreview(c);
  const placementCount = c.assets.reduce(
    (total, asset) => total + asset.placement_tags.length,
    0,
  );
  return (
    <div className="panel cr-card" role="button" tabIndex={0} aria-label={`View creative ${c.name}`} onKeyDown={event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();onOpen();}}} onClick={onOpen} style={{overflow:'hidden',cursor:'pointer'}}>
      <div className="poster" style={{background:preview ? 'var(--bg-sunk)' : poster.bg,color:poster.fg,aspectRatio:'4/3'}}>
        {preview ? (
          preview.mediaKind === 'video'
            ? <video src={preview.url} muted loop playsInline className="poster-media"/>
            : <img src={preview.url} alt="" loading="lazy" className="poster-media"/>
        ) : (
          <>
            <div className="stripes"/>
            <div className="pt" style={{fontSize:14,color:poster.fg}}>{poster.initials}</div>
          </>
        )}
      </div>
      <div className="creative-card-caption" style={{padding:10}}>
        <div className="cr-card-name" style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:12.5,letterSpacing:'.02em',textTransform:'uppercase',lineHeight:1.15}}>
          {c.name}
        </div>
        <div className="muted mt-1" style={{fontSize:10.5,fontFamily:'var(--font-mono)',letterSpacing:'.05em'}}>{creativeSpecDisplay(c)}</div>
        {c.channel === 'dsp' && c.assets.some(asset => displayAnimationExceedsLimit(asset.animation)) && (
          <span className="tag warn mt-1" title="A banner loops forever or animates past The Trade Desk's 15 seconds. Open the creative for details.">
            Animation over 15 s
          </span>
        )}
        {/* The state lives in the caption at every width (F4 UX C5): drawn over the artwork it
            collided with the artwork's own text. */}
        <div className="cr-caption-status mt-2" style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <StatusPill status={c.status}/>
          <VersionTag c={c}/>
          <ChannelPill channel={c.channel}/>
          {c.channel === 'meta'
            ? <span className="tag">{placementCount} PLACEMENT{placementCount===1?'':'S'}</span>
            : <span className="tag">{c.format}</span>}
          {c.media_type === 'video' && <span className="tag">▶ Video</span>}
          {c.status === 'approved' && c.sync && c.sync.status !== 'synced' && (
            <span className="tag warn">{c.sync.status === 'failed' ? 'Publish failed' : 'Publishing…'}</span>
          )}
        </div>
        {/* `pinned_by` is the published campaigns flying this creative — the
            same rows the drawer names. `last_used_at` has no writer yet, so
            without the pins every card read "No campaigns yet" (prod, 2026-09-12). */}
        <div className="mt-2 cr-card-usage" style={{fontFamily:'var(--font-mono)',fontSize:10,color:'var(--ink-3)',letterSpacing:'.04em'}}>
          {creativeUsageLine(c)}
        </div>
      </div>
    </div>
  );
}


/// One line under the card: the published campaigns that fly this creative,
/// first by name, the rest counted; a bare `last_used_at` when nothing pins it
/// now; otherwise the honest "No campaigns yet".
export function creativeUsageLine(c: Pick<Creative, 'pinned_by' | 'last_used_at'>): string {
  const [first, ...rest] = c.pinned_by ?? [];
  if (first) {
    const status = campaignStatusLabel(campaignPinKind(first));
    return rest.length
      ? `In ${first.campaign_name} · ${status} +${rest.length} more`
      : `In ${first.campaign_name} · ${status}`;
  }
  return c.last_used_at ? 'Used ' + fmtDate(c.last_used_at) : 'No campaigns yet';
}


export interface CreativeLibraryProps {
  /** Accepted from the route's view shim; the library navigates by `Link`. */
  setView?: (viewId: string) => void;
  /** A creative to announce as just created, with a dismiss. */
  flashId?: string | undefined;
  clearFlash?: () => void;
  /** `?focus=<id>` opens that creative's drawer. */
  focus?: string | undefined;
  /** `?tab=`, applied to the focused creative's drawer only. */
  initialTab?: CreativeDrawerTab;
}

function CreativeLibrary({ flashId, clearFlash, focus, initialTab = 'details' }: CreativeLibraryProps) {
  const { data: creatives = [], isLoading } = useCreatives();
  const { data: productions = [] } = useProductions();
  const productionMap: ProductionMap = useMemo(() => Object.fromEntries(productions.map(s => [s.id, s])), [productions]);
  // Only a production that has a creative can change what the filter shows, so
  // the options are exactly those, never a slice of the catalogue: the first 8
  // of 599 productions left three of the four with creatives unselectable.
  const productionOpts = useMemo(() => {
    const ids = [...new Set(creatives.map(c => c.production_id).filter(Boolean))];
    const withCreatives = ids
      .map(id => productionMap[id] ?? { id, name: id })
      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }));
    return [{ id: 'all', name: 'All productions' }, ...withCreatives];
  }, [creatives, productionMap]);

  const [channel, setChannel] = useState<LibraryChannelFilter>('all');
  const [status, setStatus] = useState<LibraryStatusFilter>('all');
  const [productionId, setProductionId] = useState<string>('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Creative | null>(null);
  // A link into one creative's drawer (`?focus=<id>&tab=versions`), the way
  // the pixels screen opens a pixel from an alert. Selection stays local after
  // that: opening from a card is not a route transition.
  useEffect(() => {
    if (!focus) return;
    const hit = creatives.find(c => c.id === focus);
    if (hit) setSelected(hit);
  }, [focus, creatives]);
  // The open drawer holds a snapshot of one row of this list. When the list
  // comes back — after any creative mutation, and after the content editor's
  // 409 invalidates `creatives.list` — hand the drawer the fresh row, or
  // "close and reopen the editor" reopens the same stale version it refused.
  // The drawer itself keeps whatever is half-typed in it (see
  // CreativeDetailDrawer).
  useEffect(() => {
    setSelected(open => (open ? (creatives.find(c => c.id === open.id) ?? open) : open));
  }, [creatives]);

  const filtered = creatives.filter(c =>
    (channel === 'all' || c.channel === channel) &&
    (status === 'all' ? c.status !== 'archived' : c.status === status) &&
    (productionId === 'all' || c.production_id === productionId) &&
    (!q || c.name.toLowerCase().includes(q.toLowerCase()))
  );

  const stats = {
    total:    creatives.length,
    approved: creatives.filter(c => c.status === 'approved').length,
    review:   creatives.filter(c => c.status === 'review').length,
  };

  return (
    <div>
      <div className="page-hd">
        <div>
          <h1 className="page-title">Creative Library</h1>
        </div>
        <PageActions>
          <button
            className="btn"
            onClick={()=>downloadCreativeCsv(
              filtered,
              `creatives-${fmtDateInput(Date.now())}.csv`,
            )}
            disabled={filtered.length === 0}
          ><span>{Icons.dl}</span>Export {filtered.length || ''}</button>
          <Link className="btn btn-primary" data-phone="inline" to="/creatives/new" preload="intent"><span>{Icons.plus}</span><span className="phone-long">New creative</span><span className="phone-short">New</span></Link>
        </PageActions>
      </div>

      {flashId && (() => {
        const flashed = creatives.find(c => c.id === flashId);
        if (!flashed) return null;
        return (
          <div style={{margin:'12px 22px 0', padding:'10px 14px', display:'flex', alignItems:'center', gap:10, background:'color-mix(in oklch, var(--good) 12%, var(--bg-elev))', border:'1px solid color-mix(in oklch, var(--good) 40%, var(--line))', borderRadius:'var(--r)'}}>
            <span style={{color:'var(--good)', fontSize:14}}>{Icons.check}</span>
            <span style={{fontSize:12.5}}>Creative <b>{flashed.name}</b> created · {flashed.channel === 'meta' ? 'Meta' : flashed.channel === 'ctv' ? 'CTV' : 'Display'} · {CREATIVE_STATUS_WORDS[flashed.status]}</span>
            <button type="button" className="btn btn-ghost btn-sm" aria-label="Dismiss" style={{marginLeft:'auto', padding:'2px 8px'}} onClick={clearFlash}>{Icons.x}</button>
          </div>
        );
      })()}

      {/* No "Lifetime impr." tile: nothing fills it, so it read "—" for every library. */}
      <ResponsiveKpis><div className="kpi-strip" style={{gridTemplateColumns:'repeat(4, 1fr)'}}>
        {[
          ['Assets',         stats.total],
          ['Approved',       stats.approved],
          ['In review',      stats.review],
          ['Productions covered',  new Set(creatives.map(c=>c.production_id)).size],
        ].map(([k,v]) => (
          <div className="kpi" key={k}>
            <div className="kpi-label">{k}</div>
            <div className="kpi-val">{v}</div>
          </div>
        ))}
      </div></ResponsiveKpis>

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0'}}>
          <span>{Icons.search}</span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter creative by name"/>
        </div>
        <Dropdown value={channel} options={LIB_CHANNEL_OPTS} onChange={setChannel} label="Channel"/>
        <Dropdown value={status}  options={LIB_STATUS_OPTS}  onChange={setStatus}  label="Status"/>
        <ScopeSearchPicker
          label="PRODUCTION"
          items={productionOpts}
          value={productionId}
          onChange={setProductionId}
          placeholder="All productions"
          searchPlaceholder="Search productions…"
          getKey={s => s.id}
          getSearchText={s => s.name}
          renderRow={s => (
            <>
              <span style={{width:8,height:10,borderRadius:1,background:'var(--ink-3)',display:'inline-block',flex:'0 0 auto'}}/>
              <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.name}</span>
            </>
          )}
        />
      </ResponsiveFilters>

      {isLoading && creatives.length === 0 && (
        <div className="muted" style={{padding:60,textAlign:'center'}}>Loading creatives…</div>
      )}
      {!isLoading && creatives.length === 0 && (
        <div className="muted" style={{padding:60,textAlign:'center'}}>No creatives yet. Create one to get started.</div>
      )}
      {creatives.length > 0 && (
        <CreativeGrid items={filtered} productionMap={productionMap} setSelected={setSelected}/>
      )}

      {selected && (
        <CreativeDetailDrawer
          creative={selected}
          production={productionMap[selected.production_id]}
          initialTab={selected.id === focus ? initialTab : 'details'}
          onClose={()=>setSelected(null)}
          onUpdated={setSelected}
          onDeleted={()=>setSelected(null)}
        />
      )}
    </div>
  );
}

// Virtualized card grid. Uses useVirtualGrid which computes col count from
// container width and only mounts visible-row × col cards. Each card is
// absolute-positioned via top/left so empty rows don't take DOM space.
export interface CreativeGridProps {
  items: readonly Creative[];
  productionMap: ProductionMap;
  setSelected: (creative: Creative) => void;
}

function CreativeGrid({ items, productionMap, setSelected }: CreativeGridProps) {
  const adaptive = useAdaptive();
  return adaptive ? <AdaptiveCreativeGrid items={items} productionMap={productionMap} setSelected={setSelected}/>
    : <DesktopCreativeGrid items={items} productionMap={productionMap} setSelected={setSelected}/>;
}

function AdaptiveCreativeGrid({ items, productionMap, setSelected }: CreativeGridProps) {
  const scroll = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);
  useEffect(() => {
    const node=scroll.current;
    if(!node)return;
    const measure=()=>setColumns(window.innerWidth<768 ? 1 : Math.max(1,Math.min(3,Math.floor((node.clientWidth-16)/296))));
    const observer=new ResizeObserver(measure);observer.observe(node);measure();
    return()=>observer.disconnect();
  },[]);
  const list = useVirtualizer({ count: Math.ceil(items.length/columns), getScrollElement: () => scroll.current,
    getItemKey: index => items[index*columns]?.id ?? index, estimateSize: () => 420, overscan: 3 });
  return <div className="vgrid-wrap adaptive-creative-list" ref={scroll} role="region" aria-label="Creative library" tabIndex={0}>
    {!items.length ? <div className="mobile-record-empty">No creative matches these filters.</div>
      : <div role="list" style={{height:list.getTotalSize(),position:'relative'}}>
        {list.getVirtualItems().map(item => {
          const start=item.index*columns;
          return <div key={item.key} data-index={item.index} ref={list.measureElement} role="presentation"
            style={{position:'absolute',width:'100%',top:0,left:0,transform:`translateY(${item.start}px)`,paddingBottom:16,display:'grid',gridTemplateColumns:`repeat(${columns},minmax(0,1fr))`,gap:16}}>
            {items.slice(start,start+columns).map((creative,index)=><div key={creative.id} role="listitem" aria-posinset={start+index+1} aria-setsize={items.length}>
              <CreativeCard c={creative} production={productionMap[creative.production_id]} onOpen={()=>setSelected(creative)}/>
            </div>)}
          </div>;
        })}
      </div>}
  </div>;
}

// The caption under a 4:3 poster: 10px padding, a name clamped to two lines,
// the spec line, a usage line clamped to three ("In <campaign> · status
// unverified" runs to three on a 230px card), 10px padding. The card fills its
// slot (styles.css `.vgrid-card > .cr-card`) and the caption clips, so a slot
// two pixels short shows an ellipsis, never a cut border.
const CARD_MIN_W = 220;
const CAPTION_H = 146;
const cardHeightFor = (cardW: number) => Math.round(cardW * 3 / 4) + CAPTION_H;

function DesktopCreativeGrid({ items, productionMap, setSelected }: CreativeGridProps) {
  const GAP = 12;
  const PAD = 18;
  const { visible, startIdx, cols, cardW, cardH, totalH, containerRef } = useVirtualGrid({
    items, cardMinW: CARD_MIN_W, cardH: cardHeightFor, gap: GAP,
  });
  if (items.length === 0) {
    return <div className="muted" style={{padding:60,textAlign:'center'}}>No creative matches these filters.</div>;
  }
  return (
    <div className="vgrid-wrap" ref={containerRef} role="region" aria-label="Creative library" tabIndex={0} style={{padding: PAD}}>
      <div className="vgrid" style={{height: totalH}}>
        {visible.map((c, vi) => {
          const i = startIdx + vi;
          const col = i % cols;
          const row = Math.floor(i / cols);
          const x = col * (cardW + GAP);
          const y = row * (cardH + GAP);
          return (
            <div key={c.id} className="vgrid-card"
                 style={{
                   transform: `translate(${x}px, ${y}px)`,
                   width: cardW + 'px',
                   height: cardH + 'px',
                 }}>
              <CreativeCard c={c} production={productionMap[c.production_id]} onOpen={()=>setSelected(c)}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { CreativeLibrary, CreativeCard, CreativeDetailDrawer, StatusPill, VersionTag };
export type { CreativeDetailDrawerProps, CreativeDrawerTab } from './library-drawer';
